import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackEntityView, trackFilter } from "@/lib/tracker";
import { overallBot } from "@/lib/liveProfile";
import { BlurCtx } from "@/lib/c360Mine";
import { useDemoBlur } from "@/lib/demoBlur";
import RoiCalculator from "@/components/RoiCalculator";
import { LiveHeader } from "@/components/live/LiveHeader";
import { LiveBotStrip } from "@/components/live/LiveBotStrip";
import { LivePerformance } from "@/components/live/LivePerformance";
import { LiveMix } from "@/components/live/LiveMix";
import { LiveHistory } from "@/components/live/LiveHistory";
import { BotLockedSection, openBotCheck } from "@/components/BotCheck";
import { ReserveCheckSlot } from "@/components/admin/ReserveCheckSlot";
import BackLink from "@/components/BackLink";
import { HideCreatorButton } from "@/components/HideCreatorButton";
import { useAuth } from "@/context/AuthContext";

const track = (k, v) => { try { trackFilter("live_profile", k, v); } catch (_) {} };

function Deal({ name }) {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="lp-roi">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="lp-roi-toggle" aria-expanded={open}
        className="w-full border hairline rounded-md px-5 py-4 flex items-center justify-between hover:border-cyan-400/40 transition-colors duration-150" style={{ background: "var(--surface)" }}>
        <span className="flex items-center gap-3">
          <Calculator size={16} style={{ color: "var(--primary)" }} />
          <span className="text-left">
            <span className="block display text-base font-semibold">Model a Deal</span>
            <span className="block mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">Calculate ROI, Get Instant Suggestions</span>
          </span>
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
      </button>
      {open && <div className="mt-3"><RoiCalculator subject={name} testidPrefix="lp-roi" /></div>}
    </section>
  );
}

// One live-profile open = one trackEntityView, page or drawer alike.
export function useLiveData(id, enabled, inDrawer = false) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [prof, setProf] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setData(null); setProf(null); setErr(false);
    api.get(`/live-intel/creator/${encodeURIComponent(id)}`).then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      if (!d.ready) return;
      try { trackEntityView("creator", id, d.creator.name, "live_profile"); } catch (_) {}
      const unaff = d.creator.segment === "unaffiliated";
      api.get(unaff ? `/unaffiliated/creator/${encodeURIComponent(id)}` : `/kols/${encodeURIComponent(id)}`)
        .then(({ data: p }) => { if (alive) setProf(unaff ? { ...p, _c: p.creator } : { ...p, _c: p }); })
        .catch(() => { if (alive) setProf({ _c: {} }); });
    }).catch(() => { if (!alive) return; if (inDrawer) setErr(true); else nav("/", { replace: true }); });
    return () => { alive = false; };
  }, [id, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, prof, err };
}

// v83 — the live-streamer profile's content, shared by the full page and the drawer.
export function LiveProfileBody({ id, enabled, inDrawer = false, onLeave, onClose, className = "" }) {
  const { data, prof, err } = useLiveData(id, enabled, inDrawer);
  const { isBlurred } = useDemoBlur();
  const { user } = useAuth();
  const [rev, setRev] = useState(null);
  useEffect(() => { setRev(null); }, [id]);
  const [ui, setUi] = useState({ period: "90d", cadence: "weekly", metric: "lives", type: "bar" });
  const set = useMemo(() => ({
    period: (k) => { setUi((s) => ({ ...s, period: k, cadence: k === "1d" || k === "7d" ? "daily" : s.cadence })); track("period", k); },
    cadence: (k) => { setUi((s) => ({ ...s, cadence: k })); track("cadence", k); },
    metric: (k) => { setUi((s) => ({ ...s, metric: k })); track("metric", k); },
    type: (k) => { setUi((s) => ({ ...s, type: k })); track("chart_type", k); },
  }), []);

  if (err) return <div className="py-16 text-zinc-500" data-testid="lp-error">This live profile could not be opened.</div>;
  if (!data) return <div className="py-16 flex items-center gap-3 text-zinc-500" data-testid="lp-loading"><Loader2 className="animate-spin" size={16} /> Loading live streamer profile…</div>;
  if (!data.ready) return <div className="py-16 text-zinc-500" data-testid="lp-not-ready">Live data is being refreshed, please check back in a few minutes.</div>;

  const c = data.creator;
  const feats = data.features;
  const pc = prof?._c;
  // v67 — clients open Bot Check on purpose; nothing about the verdict shows before.
  const subLocked = !!pc?.bot_check_locked;
  const liveLocked = !!data.bot_locked;
  const locked = (subLocked || liveLocked) && !rev;
  const sub = !pc ? undefined : subLocked ? (rev ? rev.sub : undefined) : pc.bot_check;
  const live = !feats.bot ? undefined : liveLocked ? (rev ? rev.live : undefined) : c.bot;
  const reveal = async () => {
    const d = await openBotCheck([id], "live_profile");
    setRev({ sub: d.items?.[id] ?? null, live: d.live?.[id] || "not_enough" });
  };
  const both = !!pc && sub !== undefined && live !== undefined;
  const overall = overallBot(sub?.verdict, live);
  const p = data.live.periods[ui.period];
  const blurred = isBlurred(c.id);

  return (
    <BlurCtx.Provider value={blurred}>
      <div className={`flex flex-col gap-5 ${className}`} data-testid={inDrawer ? "live-profile-drawer-body" : "live-profile-body"} data-creator={c.id} data-blurred={blurred ? "1" : "0"}>
        {!inDrawer && (
          <div className="flex items-center justify-between gap-3">
            <BackLink testid="lp-back" label="Back to Live Streaming Intel" fallback="/live-streaming-intel" className="text-sm" />
            {user?.is_super_admin && <HideCreatorButton id={c.id} name={blurred ? "this creator" : c.name} label="Hide" />}
          </div>
        )}
        <ReserveCheckSlot id={c.id} />
        <LiveHeader c={c} prof={pc} feats={feats} perWeek={data.live.per_week} overall={overall} showOverall={both} onLeave={onLeave} onClose={onClose} />
        {pc && (locked ? <BotLockedSection onOpen={reveal} testid="lp-bot-locked" /> : <LiveBotStrip sub={sub} live={live} overall={overall} />)}
        <LivePerformance c={c} live={data.live} ui={ui} set={set} />
        <LiveMix c={c} prof={prof} />
        <Deal name={blurred ? "this creator" : c.name} />
        <LiveHistory lives={data.live.lives} count={p.count} period={ui.period} />
      </div>
    </BlurCtx.Provider>
  );
}
